import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../client/query-client';
/**
 * Changing your own password and email.
 *
 * Proposed in round 3, planned, dropped, and finally built round 12. Until then
 * a member could not change their own password at all — on a shared family
 * device that is the first control somebody reaches for, which is why it was
 * argued as an account-security gap rather than a parity one.
 */
export const ChangePasswordResult = z.object({
    /** Whether THIS session survived. See the note on `refreshToken` below. */
    currentSessionKept: z.boolean(),
});
/**
 * ── Why the refresh token is sent ─────────────────────────────────────────
 * The route signs out every OTHER session — that is the point of it on a shared
 * device. But an access token carries no link to the refresh family beside it,
 * so without being told which family is calling, the API cannot tell this
 * session from the others and ends them all.
 *
 * Naming our own refresh token is therefore how the member stays signed in on
 * the device they are typing on. Omitting it would log them out of the very
 * screen they just used, which reads as the change having failed.
 *
 * The store is passed rather than the token so nothing above this layer has to
 * touch a refresh token to call a password form.
 */
export function useChangePassword(http, auth) {
    return useMutation({
        mutationFn: (input) => http.post('/me/password/change', { ...input, refreshToken: auth.tokens?.refreshToken }, { schema: ChangePasswordResult }),
        /*
         * ── `currentSessionKept: false` means clear NOW, not on the next 401 ───
         * Revocation is of refresh-token FAMILIES. An access token already issued
         * is not checked against a list and keeps working until it expires — up to
         * fifteen minutes. So a session that has genuinely ended goes on loading
         * pages, and the member reads "every session was signed out" over a shell
         * that is plainly still signed in.
         *
         * Waiting for the 401 is waiting a quarter of an hour to tell the truth.
         */
        onSuccess: (result) => {
            if (!result.currentSessionKept)
                auth.clear();
        },
    });
}
export const EmailChangeRequested = z.object({
    newEmail: z.string(),
    /** When the mailed link stops working. Shown, so "check your mail" has a clock. */
    expiresAt: z.string(),
});
/**
 * Step one of two. **Nothing changes yet.**
 *
 * The request proves you hold the account (it re-asks for the password), the
 * link proves you hold the mailbox, and the contract is explicit that neither
 * alone is enough to take an account over. So this hook cannot be the whole
 * flow no matter how much simpler that would be — `useConfirmEmailChange` is
 * the half that actually switches the address.
 *
 * Nothing is sent to the old address and nothing about it is echoed back, so
 * there is no "we told your old mailbox" line to render.
 */
export function useRequestEmailChange(http) {
    return useMutation({
        mutationFn: (input) => http.post('/me/email/change', input, { schema: EmailChangeRequested }),
    });
}
export const EmailChangeResult = z.object({
    email: z.string(),
    sessionsRevoked: z.number().int().nonnegative(),
    currentSessionKept: z.boolean(),
});
/**
 * Step two — and it is **unauthenticated on purpose**.
 *
 * The link is opened from the new mailbox, very often on a different device or
 * a different browser than the one that asked. Demanding a session there would
 * make the flow unfinishable for exactly the people it exists for. The token in
 * the link is the capability.
 *
 * It is a POST rather than a GET because a mail scanner following links unbidden
 * would spend a single-use token before the member ever saw it. That is why the
 * mail points at a PAGE of ours that posts, rather than at the API.
 *
 * ── Why this is not `useMutation` ─────────────────────────────────────────
 * Every other write in this package is, and this one cannot be. A mutation is
 * something a person triggers; here **arriving is the action**, so it has to
 * fire from an effect on mount — and that is exactly where a mutation observer
 * comes apart. React's development double-mount unsubscribes the first
 * observer, the request carries on detached, and the remounted observer reports
 * `idle` forever. The screen sits on "one moment" while the token has already
 * been spent. It is a real hang, not a development artefact, and we watched it
 * happen.
 *
 * So the request lives outside React and the component holds three fields of
 * state. `IN_FLIGHT` is keyed by token rather than a boolean: two mounts of the
 * same link must share one promise — the token is single-use, and the second
 * call would 400 on the token the first one just spent and tell the member
 * their working link was dead.
 */
const IN_FLIGHT = new Map();
export function confirmEmailChange(http, token, auth) {
    const existing = IN_FLIGHT.get(token);
    if (existing)
        return existing;
    /* The client attaches a bearer if one happens to exist. That is harmless
     * here — the route does not read it, the token in the body is the whole
     * capability — and stripping it would mean a second code path through the
     * client for one call. */
    const call = http.post('/me/email/confirm', { token, refreshToken: auth?.tokens?.refreshToken }, { schema: EmailChangeResult });
    /* Cached on failure too, deliberately. A spent token does not become unspent,
     * so a remount replaying the same link would only earn a second identical
     * refusal — and would look, in the log, like the member clicked twice. */
    IN_FLIGHT.set(token, call);
    return call;
}
export const PasswordResetRequested = z.object({ ok: z.literal(true) });
/**
 * "I forgot my password" — step one.
 *
 * ── It always answers `ok: true` ──────────────────────────────────────────
 * Whether or not the address exists. That is the point, not an oversight: an
 * endpoint that says "no such account" is an endpoint that confirms which
 * addresses hold accounts here, one guess at a time. On a product about
 * parenting, membership is itself sensitive — somebody's ex-partner should not
 * be able to establish that they signed up.
 *
 * The consequence for the UI is that there is no failure state to render and no
 * success state either. The screen says "if that address has an account, we have
 * sent it a link" **before** and after, and means it.
 */
export function useRequestPasswordReset(http) {
    return useMutation({
        mutationFn: (email) => http.post('/me/password/reset', { email }, { schema: PasswordResetRequested }),
    });
}
export const PasswordResetResult = z.object({ sessionsRevoked: z.number().int().nonnegative() });
/**
 * Step two, from the link.
 *
 * ── Every session ends, and there is no keeping one ───────────────────────
 * Unlike a password CHANGE, this takes no `refreshToken` and offers no way to
 * stay signed in anywhere. The backend's reasoning is the right one: somebody
 * resetting a password may be recovering from a compromise, so there is no
 * session on this account that can be trusted — including the one that just
 * asked. `sessionsRevoked` is a count, not a choice.
 *
 * So the screen after this is always "sign in", and unlike the email-change
 * confirmation there is no branch where it is not.
 *
 * ── `fullName` is for ACCEPTING, not resetting ────────────────────────────
 * The same route accepts an organisation seat invitation, because for somebody
 * who has never had a password, setting one is the whole of accepting. Such an
 * account is created from an address and has **no name**, so the screen that
 * collects the password collects a name beside it.
 *
 * The server fills `fullName` **only when it is empty** — a `where` clause, not
 * a check. That is what makes the field safe to exist at all: a reset token
 * proves somebody holds a mailbox, and without that condition anyone who could
 * read an inbox could rename its owner. A name sent for an account that already
 * has one is ignored rather than refused, so nobody is stranded over a field
 * they never had to send.
 *
 * Which is why only the invitation screen shows it. Sending it from a reset
 * would be sending something that is discarded.
 */
/** Mirrors the backend's `PasswordResetAccount`. */
export const PasswordResetAccount = z.object({ email: z.string() });
/**
 * `POST /api/me/password/reset/inspect` — which account a reset link is for.
 *
 * Reads only: the token stays unused. The « choose a new password » page shows
 * the address, and learns that a link is spent or expired before anybody types
 * a password into it. Not retried: a refused link stays refused.
 */
export function useInspectPasswordReset(http, token) {
    return useQuery({
        queryKey: ['password-reset', 'inspect', token],
        queryFn: () => http.post('/me/password/reset/inspect', { token }, { schema: PasswordResetAccount }),
        enabled: Boolean(token),
        retry: false,
        staleTime: Infinity,
        /*
         * Read once, and never again for this page (29 Oct 2026). A refused link
         * holds no data, and a query with no data counts as stale whatever
         * `staleTime` says — so focusing the window re-asked. Opening an expired
         * invitation mails a fresh link, which SUPERSEDES the one in the address
         * bar: the second ask then answered « replaced », and the page flipped
         * from « we've just sent you a new link » to « this link no longer works »
         * the moment somebody clicked into the email field, wiping what they typed.
         */
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        // An errored query is re-asked whenever a component mounts it again unless
        // this is off — and the public shell remounts the page around it.
        retryOnMount: false,
    });
}
export function useConfirmPasswordReset(http) {
    return useMutation({
        mutationFn: (input) => http.post('/me/password/reset/confirm', input, { schema: PasswordResetResult }),
    });
}
export const LocalePreference = z.object({ locale: z.string().nullable() });
/**
 * Set — or clear — the language the platform writes to you in.
 *
 * ── This is the other half of a control that only had one ─────────────────
 * Our language toggle was `localStorage` only, which was defensible while the
 * interface language had no server-side consequence. It stopped being true the
 * day transactional mail began resolving from `users.locale`: the language
 * somebody reads the product in and the language their password-reset email
 * arrives in became one setting with only one half reachable.
 *
 * ── `null` clears it, and clearing is not the same as never having chosen ──
 * Both resolve to the message's source locale, so they behave alike — but only
 * one of them is a decision, and the API is not the place to foreclose a
 * control we have not built yet.
 *
 * ── `POST /me/locale/update`, not `PATCH /me` ─────────────────────────────
 * This API serves GET and POST only, and writes carry an action segment. With
 * PUT/PATCH/DELETE gone, *GET is safe* is the one guarantee the protocol still
 * gives — the same reasoning that makes the email-confirm link a POST.
 *
 * No idempotency key, deliberately on their side: this writes one field to a
 * value the caller named, so a repeat sets it to the same thing and requiring a
 * header would refuse the honest retry a dropped connection produces.
 */
export function useSetLocale(http) {
    return useMutation({
        mutationFn: (locale) => http.post('/me/locale/update', { locale }, {
            schema: LocalePreference,
            /*
             * The press and the navigation are one gesture — switching language
             * moves the member to the other language's URL, and that navigation
             * would otherwise abort this request. `keepalive` lets the write
             * finish after the page is gone, so the URL changes immediately and
             * the preference still lands.
             */
            keepalive: true,
        }),
    });
}
/**
 * Newsletter consent — `POST /me/marketing-consent/update`.
 *
 * The newsletter is Kit's, not this platform's own mail: saying no here
 * unsubscribes them there, and never touches receipts, resets or the
 * onboarding letters. The session is refreshed so `marketingConsent` on it
 * is what the toggle reads next.
 */
export const MarketingConsentPreference = z.object({
    consent: z.boolean(),
    since: z.string().nullable(),
});
export function useSetMarketingConsent(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (consent) => http.post('/me/marketing-consent/update', { consent }, { schema: MarketingConsentPreference }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.session }),
    });
}
/** What `POST /me/deletion` answers. */
export const AccountDeletionRequested = z.object({
    requestedAt: z.string(),
    /** When the erasure falls due — 30 days on. */
    dueAt: z.string(),
    subscriptionsCancelled: z.number().int().nonnegative(),
    sessionsRevoked: z.number().int().nonnegative(),
});
/**
 * « Supprimer mon compte ».
 *
 * The server switches the account off and ends every session, this one
 * included — but it does NOT clear the local session here, unlike
 * `useChangePassword`. The screen has one more thing to say (« your account
 * will be erased on … ») and clearing now would unmount it mid-sentence. The
 * screen signs out when the member dismisses that message; the access token it
 * is still holding dies on its own within minutes either way.
 */
export function useRequestAccountDeletion(http) {
    return useMutation({
        mutationFn: (currentPassword) => http.post('/me/deletion', { currentPassword }, { schema: AccountDeletionRequested }),
    });
}
/** Why a deletion request was refused, as an i18n key under `account.`. */
export function accountDeletionRefusalKey(error) {
    const code = error?.code;
    if (code === 'CURRENT_PASSWORD_INCORRECT')
        return 'account.passwordWrong';
    if (code === 'ACCOUNT_DELETION_NOT_SELF_SERVICE')
        return 'account.deleteStaff';
    if (code === 'ACCOUNT_DELETION_ALREADY_REQUESTED')
        return 'account.deleteAlready';
    return 'account.deleteFailed';
}
//# sourceMappingURL=credentials.js.map